// AppBuilderLayoutComponent.tsx
import "./UniversalAppBuilderLayoutComponent.scss";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PROJECTS_ROUTE } from "@/constants/RoutesConfig.tsx";
import { useDispatch, useSelector } from "react-redux";
import {
    resetProjectDetails,
    setCurrentChatHistories,
    setCurrentProjectDetails,
    setIframeUrl,
    setProjectCode, updateProjectDetails
} from "@/store/actions/projectdata.action.ts";
import { CommonService } from "@/services";
import { useWakeLock } from "@/hooks/use-wave-lock.ts";
import UniversalAppBuilderComponent from "./universal-app-builder/UniversalAppBuilderComponent";
import SupabaseComponent from "@/screens/app/project/universal-app-builder-layout/tools/supabase/SupabaseComponent.tsx";
import { AppType } from "@/models/project.model.ts";
import commonService from "@/services/common.service.ts";
import PageLoaderComponent from "@/components/common-components/page-loader/pageLoaderComponent.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { projectsListKey } from "@/screens/app/projects-list/ProjectsComponent";
import ProjectNotFoundComponent from "@/components/common-components/project-not-found/ProjectNotFoundComponent";
import { IAPIResponseType } from "@/models/api.model.ts";
import { skip } from "rxjs/operators";
import { useSocket } from "@/services/hooks/useSocket.ts";
import GrapesJSAutoSaveHandler from "./universal-app-builder/app-tabs/app-code-and-preview/preview-renderer/sandpack-html-preview/components/GrapesJSAutoSaveHandler";
import SettingsComponent from "../settings/SettingsComponent";
import { Misc } from "@/constants";

const toolConfigurations = {
    "html": ["supabase"],
    "nextjs": ["supabase"],
    "streamlit": [],
}

export interface RouteParams {
    projectId: string;
}

enum TitleEventType {
    TITLE_GENERATED = "title.generated",
}


const AppBuilderLayoutComponent = () => {
    const { projectId } = useParams<any>();
    const currentProjectDetails = useSelector((state: any) => state.projectData.currentProjectDetails);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [isProjectDetailsLoading, setIsProjectDetailsLoading] = useState(false);
    const [isProjectNotFound, setIsProjectNotFound] = useState(false);
    const appType: AppType = useMemo(() => commonService.getAppType(location.pathname), []);
    const currentAppConfiguration = toolConfigurations[appType];
    const [isSupabaseDialogOpen, setIsSupabaseDialogOpen] = useState(false);

    

    // For stopping the device from sleeping
    useWakeLock();

    const { events } = useSocket({
        eventTypes: [
            TitleEventType.TITLE_GENERATED,
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'title-event-listener',
        debug: false, // Set to true only during development for detailed logs
    }); 


    useEffect(() => {
        const sub = CommonService._communications.setProjectUrlSubject.subscribe((value: string) => {
            if(value) {
                dispatch(setIframeUrl(currentProjectDetails?.preview_url));
            }
        });
        return () => sub.unsubscribe();
    }, [currentProjectDetails,dispatch]);

    useEffect(() => {
        const titleEvent = events[TitleEventType.TITLE_GENERATED]
        const title = titleEvent?.event_meta?.name;
        if (title && projectId) {
            dispatch(updateProjectDetails({ name: title }))
            
            // Update projects list cache directly with new name
            queryClient.setQueryData([projectsListKey], (oldData: any) => {
                if (!oldData || !Array.isArray(oldData)) return oldData;
                
                return oldData.map((project: any) => 
                    project.id === projectId 
                        ? { ...project, name: title }
                        : project
                );
            });
        }
    }, [events, dispatch, queryClient, projectId]);


    const handleExitChat = useCallback(() => {
        setIsSupabaseDialogOpen(false);
        localStorage.removeItem(`${Misc.LOCAL_STORAGE_ADVANCED_MODE_PREFIX}${projectId}`);
        setTimeout(() => {
            navigate(PROJECTS_ROUTE);
            dispatch(resetProjectDetails());
        }, 100);
        CommonService.handleMixpanelTrack("project_exit_chat",{
            project_id: projectId,
            timestamp: new Date().toISOString()
        });
    }, [navigate, dispatch, projectId]);

    const handleSupabaseToolOpen = useCallback(() => {
        setIsSupabaseDialogOpen(true);
    }, []);

    const handleSupabaseToolClose = useCallback(() => {
        setIsSupabaseDialogOpen(false);
    }, []);





    // Listen for Supabase connection requests
    useEffect(() => {
        const sub = CommonService._communications.connectSupabaseSubject.pipe(skip(1)).subscribe((data: any) => {
            if (data) {
                handleSupabaseToolOpen();
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [handleSupabaseToolOpen]);

    // Global keyboard shortcut handler for Ctrl+S / Cmd+S
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Ctrl+S or Cmd+S to trigger commit dialog
            if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault();

                // Trigger commit dialog via communication service
                CommonService._communications.triggerCommitDialogSubject.next(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const fetchProjectDetails = useCallback((payload: any) => {
        setIsProjectDetailsLoading(true);
        setIsProjectNotFound(false);
        
        // PRIORITY 1: Fetch project details first (critical for UI rendering)
        commonService._project_service.getProjectDetails(payload, {})
            .then((response: IAPIResponseType<any>) => {
                dispatch(setCurrentProjectDetails(response?.data?.project))
                dispatch(setIframeUrl(response?.data?.project?.preview_url))
                
                
                // PRIORITY 2: Fetch chat history in background (needed for chat UI)
                commonService._project_service.getProjectChatHistories(payload, {})
                    .then((chatResponse: IAPIResponseType<any>) => {
                        dispatch(setCurrentChatHistories(chatResponse?.data || []))
                        setIsProjectDetailsLoading(false);

                        CommonService._communications.scrollToBottomOfChatSubject.next(true)
                        setTimeout(() => {
                            CommonService._communications.scrollToBottomOfChatSubject.next(true)
                        }, 100)
                    })
                    .catch((error: any) => {
                        console.error('Error fetching chat history:', error);
                        commonService._alert.showToast('Error fetching chat history');
                    });
                
                // PRIORITY 3: Defer project code fetch (heavy payload, not immediately needed)
                // Wait 500ms to avoid connection saturation with other API calls
                // NOTE: Future enhancement - implement file tree API that only fetches file names/structure
                // and loads individual file contents on-demand when user opens them in the editor.
                // This would significantly reduce initial payload size for large projects.
                setTimeout(() => {
                    commonService._project_service.getProjectCode(payload, {})
                        .then((codeResponse: IAPIResponseType<any>) => {
                            if (Object.keys(codeResponse?.data || {})?.length > 0) {
                                dispatch(setProjectCode(JSON.stringify(codeResponse?.data)))
                            }
                        })
                        .catch((error: any) => {
                            console.error('Error fetching project code:', error);
                            commonService._alert.showToast('Error fetching project code');
                            commonService.handleMixpanelTrack("project_code_fetch_error",{
                                project_id: projectId,
                                timestamp: new Date().toISOString()
                            });
                        });
                }, 500);
                
                return response;
            })
            .catch((error: any) => {
                if (error?.response?.status === 404 || 
                    error?.status === 404 || 
                    error?.message?.toLowerCase().includes('not found') ||
                    error?.response?.data?.message?.toLowerCase().includes('not found')) {
                    setIsProjectNotFound(true);
                } else {
                    commonService._alert.showToast('Error fetching project details');
                }
                CommonService.handleMixpanelTrack("project_details_fetch_error",{
                    project_id: projectId,
                    error_message: error?.message,
                    timestamp: new Date().toISOString()
                });
                setIsProjectDetailsLoading(false);
            });
    }, [dispatch, projectId])

    const fetchProjectCode = useCallback(() => {
        commonService._project_service.getProjectCode(projectId, {})
            .then((response: IAPIResponseType<any>) => {
                if (Object.keys(response?.data)?.length > 0) {
                    dispatch(setProjectCode(JSON.stringify(response?.data)))
                }
            })
            .catch(() => {
                commonService._alert.showToast('Error fetching project code');
                commonService.handleMixpanelTrack("project_code_fetch_error",{
                    project_id: projectId,
                    timestamp: new Date().toISOString()
                });
            })
    }, [dispatch, projectId])

    useEffect(() => {
        const sub = commonService._communications.fetchProjectCode.subscribe((data: any) => {
            if (data) {
                fetchProjectCode()
            }
        })
        return () => {
            sub.unsubscribe();
        }
    }, [fetchProjectCode]);

   

    useEffect(() => {
        if (projectId) {
            fetchProjectDetails(projectId)
        }
    }, [projectId, fetchProjectDetails])

    
    return (
        <div className="app-builder-layout-component">
            {
                isProjectDetailsLoading && <PageLoaderComponent />
            }
            {
                !isProjectDetailsLoading && isProjectNotFound && (
                    <ProjectNotFoundComponent projectId={projectId} />
                )
            }
            {
                !isProjectDetailsLoading && !isProjectNotFound && <>
                    <UniversalAppBuilderComponent 
                        currentAppConfiguration={currentAppConfiguration}
                        onExitChat={handleExitChat}
                        onSupobaseToolOpen={handleSupabaseToolOpen}
                    />
                    <GrapesJSAutoSaveHandler />
                    {
                        currentAppConfiguration?.map((tool: string, index: number) => {
                            if (tool === "supabase" && projectId) {
                                return <SupabaseComponent
                                    key={`${tool}-${index}`}
                                    isOpen={isSupabaseDialogOpen}
                                    onClose={handleSupabaseToolClose}
                                    projectId={projectId}
                                    currentProjectDetails={currentProjectDetails}
                                /> as ReactNode
                            }
                            return null;
                        })
                    }
                </>
            }
            <SettingsComponent />
        </div>
    );
};

export default AppBuilderLayoutComponent;
